import { objectSpread2 as _objectSpread2, objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useRef, useCallback, useEffect } from 'react';
import { Transition } from 'react-transition-group';
import useForkRef from '../../../hooks/use-fork-ref.esm.js';
import { useSafeThemeUI } from '../../../theme-provider/use-theme-ui.esm.js';
import { easing, duration } from '../../../themes/theme-keys/transitions.esm.js';
import debounce from '../../../utils/debounce.esm.js';
import ownerWindow from '../../../utils/owner-window.esm.js';
import { getTransitionProps } from '../transitions/utils.esm.js';

var _excluded = ["addEndListener", "appear", "children", "container", "direction", "easing", "in", "onEnter", "onEntered", "onEntering", "onExit", "onExited", "onExiting", "style", "timeout", "TransitionComponent"];

// Translate the node so it can't be seen on the screen.
// Later, we're going to translate the node back to its original location with `none`.
function getTranslateValue(direction, node, resolvedContainer) {
  var rect = node.getBoundingClientRect();
  var containerRect = resolvedContainer && resolvedContainer.getBoundingClientRect();
  var containerWindow = ownerWindow(node);
  var transform;

  // @ts-ignore
  if (node.fakeTransform) {
    // @ts-ignore
    transform = node.fakeTransform;
  } else {
    var computedStyle = containerWindow.getComputedStyle(node);
    transform = computedStyle.getPropertyValue('-webkit-transform') || computedStyle.getPropertyValue('transform');
  }
  var offsetX = 0;
  var offsetY = 0;
  if (transform && transform !== 'none' && typeof transform === 'string') {
    var transformValues = transform.split('(')[1].split(')')[0].split(',');
    offsetX = parseInt(transformValues[4], 10);
    offsetY = parseInt(transformValues[5], 10);
  }
  var translateValue = 0;
  switch (direction) {
    case 'left':
      translateValue = (containerRect ? containerRect.right : containerWindow.innerWidth) + offsetX - rect.left;
      break;
    case 'right':
      translateValue = containerRect ? rect.right - containerRect.left - offsetX : rect.left + rect.width - offsetX;
      break;
    case 'up':
      translateValue = (containerRect ? containerRect.bottom : containerWindow.innerHeight) + offsetY - rect.top;
      break;
    default:
      translateValue = rect.top - (containerRect ? containerRect.top : 0) + rect.height - offsetY;
      break;
  }
  return {
    translateValue: translateValue,
    translate: "translate".concat(direction === 'up' || direction === 'down' ? 'Y' : 'X', "(").concat(direction === 'right' || direction === 'down' ? '-' : '').concat(translateValue, "px)")
  };
}
function resolveContainer(containerPropProp) {
  return typeof containerPropProp === 'function' ? containerPropProp() : containerPropProp;
}
function setTranslateValue(direction, node,
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
containerProp) {
  var resolvedContainer = resolveContainer(containerProp);
  var _getTranslateValue = getTranslateValue(direction, node, resolvedContainer),
    translate = _getTranslateValue.translate;
  if (translate) {
    // eslint-disable-next-line no-console
    // console.log(`---- slide setting style.transform = ${translate} ---`)
    node.style.webkitTransform = translate;
    node.style.transform = translate;
  }
}
function getSlideTransitionValue(mode, props, theme, style) {
  var timeout = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : defaultTimeout;
  var easing = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : defaultEasing;
  var transitionProps = getTransitionProps({
    timeout: timeout,
    style: style,
    easing: easing
  }, {
    mode: mode
  });
  return theme.transitions ? theme.transitions.create(props, _objectSpread2({}, transitionProps)) : 'transform';
}
var defaultEasing = {
  enter: easing.easeOut,
  exit: easing.sharp
};
var defaultTimeout = {
  enter: duration.enteringScreen,
  exit: duration.leavingScreen
};

/**
 * The Slide transition is used by the [Drawer](/components/drawers/) component.
 * It uses [react-transition-group](https://github.com/reactjs/react-transition-group) internally.
 */
// @ts-ignore
var Slide = /*#__PURE__*/React.forwardRef(function Slide(props, ref) {
  var addEndListener = props.addEndListener,
    _props$appear = props.appear,
    appear = _props$appear === void 0 ? true : _props$appear,
    children = props.children,
    containerProp = props.container,
    _props$direction = props.direction,
    direction = _props$direction === void 0 ? 'down' : _props$direction,
    _props$easing = props.easing,
    easingProp = _props$easing === void 0 ? defaultEasing : _props$easing,
    inProp = props["in"],
    onEnter = props.onEnter,
    onEntered = props.onEntered,
    onEntering = props.onEntering,
    onExit = props.onExit,
    onExited = props.onExited,
    onExiting = props.onExiting,
    style = props.style,
    _props$timeout = props.timeout,
    timeout = _props$timeout === void 0 ? defaultTimeout : _props$timeout,
    _props$TransitionComp = props.TransitionComponent,
    TransitionComponent = _props$TransitionComp === void 0 ? Transition : _props$TransitionComp,
    other = _objectWithoutProperties(props, _excluded);
  var theme = useSafeThemeUI(props);
  var nodeRef = useRef(null);
  //@ts-ignore
  var handleRefIntermediary = useForkRef(children === null || children === void 0 ? void 0 : children.ref, nodeRef);
  var handleRef = useForkRef(handleRefIntermediary, ref);
  var normalizedTransitionCallback = function normalizedTransitionCallback(callback) {
    return function (isAppearing) {
      if (callback) {
        var node = nodeRef.current;
        if (node) {
          // onEnterXxx and onExitXxx callbacks have a different arguments.length value.
          if (isAppearing === undefined) {
            // @ts-ignore
            callback(node);
          } else {
            callback(node, !!isAppearing);
          }
        }
      }
    };
  };
  var handleEnter = normalizedTransitionCallback(function (node, isAppearing) {
    // eslint-disable-next-line no-console
    // console.log(`---- slide handleEnter ---`)
    setTranslateValue(direction, node, containerProp);
    if (onEnter) {
      onEnter(node, isAppearing);
    }
  });
  var handleEntering = normalizedTransitionCallback(function (node, isAppearing) {
    // eslint-disable-next-line no-console
    // console.log(`---- slide handleEntering ---`)

    var transition = getSlideTransitionValue('enter', 'transform', theme, style, timeout, easingProp);
    // eslint-disable-next-line no-console
    // console.log(`---- slide setting style.transition = ${transition} ---`)
    node.style.webkitTransition = transition;
    node.style.transition = transition;

    // eslint-disable-next-line no-console
    // console.log(`---- slide setting style.transform = 'none' ---`)
    node.style.webkitTransform = 'none';
    node.style.transform = 'none';
    if (onEntering) {
      onEntering(node, isAppearing);
    }
  });
  var handleEntered = normalizedTransitionCallback(function (node, isAppearing) {
    // eslint-disable-next-line no-console
    // console.log(`---- slide handleEntered ---`)
    if (onEntered) {
      onEntered(node, isAppearing);
    }
  });
  var handleExiting = normalizedTransitionCallback(function (node) {
    // eslint-disable-next-line no-console
    // console.log(`---- slide handleExiting ---`)
    if (onExiting) {
      onExiting(node);
    }
  });
  var handleExit = normalizedTransitionCallback(function (node) {
    // eslint-disable-next-line no-console
    // console.log(`---- slide handleExit ---`)
    var transition = getSlideTransitionValue('exit', 'transform', theme, style, timeout, easingProp);
    // eslint-disable-next-line no-console
    // console.log(`---- slide setting style.transition = ${transition} ---`)
    node.style.webkitTransition = transition;
    node.style.transition = transition;
    setTranslateValue(direction, node, containerProp);
    if (onExit) {
      onExit(node);
    }
  });
  var handleExited = normalizedTransitionCallback(function (node) {
    // eslint-disable-next-line no-console
    // console.log(`---- slide handleExited ---`)
    // No need for transitions when the component is hidden
    // eslint-disable-next-line no-console
    // console.log(`---- slide setting style.transition = '' ---`)
    node.style.webkitTransition = '';
    node.style.transition = '';
    if (onExited) {
      onExited(node);
    }
  });
  var handleAddEndListener = useCallback(function (next) {
    var element = nodeRef.current;
    if (addEndListener && element) {
      // Old call signature before `react-transition-group` implemented `nodeRef`
      addEndListener(element, next);
    }
  }, [addEndListener]);
  var updatePosition = useCallback(function () {
    // eslint-disable-next-line no-console
    // console.log(`---- slide updatePosition ---`)
    if (nodeRef.current) {
      setTranslateValue(direction, nodeRef.current, containerProp);
    }
  }, [direction, containerProp]);
  useEffect(function () {
    // Skip configuration where the position is screen size invariant.
    if (inProp || direction === 'down' || direction === 'right') {
      return undefined;
    }
    var handleResize = debounce(function () {
      // eslint-disable-next-line no-console
      // console.log(`---- slide handleResize ---`)
      if (nodeRef.current) {
        setTranslateValue(direction, nodeRef.current, containerProp);
      }
    });
    var containerWindow = ownerWindow(nodeRef.current);
    containerWindow.addEventListener('resize', handleResize);
    return function () {
      handleResize.clear();
      containerWindow.removeEventListener('resize', handleResize);
    };
  }, [direction, inProp, containerProp]);
  useEffect(function () {
    if (!inProp) {
      // We need to update the position of the drawer when the direction change and
      // when it's hidden.
      updatePosition();
    }
  }, [inProp, updatePosition]);
  return /*#__PURE__*/React.createElement(TransitionComponent, _extends({
    nodeRef: nodeRef,
    onEnter: handleEnter,
    onEntered: handleEntered,
    onEntering: handleEntering,
    onExit: handleExit,
    onExited: handleExited,
    onExiting: handleExiting,
    addEndListener: handleAddEndListener,
    appear: appear,
    "in": inProp,
    timeout: timeout
  }, other),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function (state, childProps) {
    return /*#__PURE__*/React.cloneElement(children, _objectSpread2({
      ref: handleRef,
      style: _objectSpread2(_objectSpread2({
        display: state === 'exited' && !inProp ? 'none' : undefined
      }, style), children.props.style)
    }, childProps));
  });
});

export default Slide;
export { getSlideTransitionValue };
//# sourceMappingURL=slide.esm.js.map
